import React from 'react';
import styled from 'styled-components';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import CreateShowingDrawerSearchStep from './CreateShowingDrawerSearchStep';
import CreateShowingDrawerDetailsStep from './CreateShowingDrawerDetailsStep';
import CtaPrimary from 'components/ui/CtaPrimary';

import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';

import { getTranslatedString } from 'helpers/i18n';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';
import Lottie from 'react-lottie';

const SLOT_SEARCH_STEP = 'slot_search';
const DETAILS_STEP = 'details';
const FORM_NAME = 'showing_form';

const Page = styled.div`
  display: ${props => `${props.show ? 'block' : 'none'}`};
`;

class CreateShowingDrawerWizard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeStep: SLOT_SEARCH_STEP,
      slotSearchData: undefined,
      loading: false,
      error: false,
      success: false,
      formData: {},
    };
  }

  handleBack = () => {
    this.setState({
      activeStep: SLOT_SEARCH_STEP,
    });
  };

  goNextStep = () => {
    const { activeStep } = this.state;
    if (activeStep === SLOT_SEARCH_STEP) {
      // A delay is required becuase otherwize it re-renders before primary action click event is handled
      // as a result, the form in the next step is submitted immediately after first render
      setTimeout(() => {
        this.setState({
          activeStep: DETAILS_STEP,
        });
      }, 0);
    }
  };

  setSlotSearchData = data => {
    this.setState({
      slotSearchData: data,
    });
  };

  handleLoadingChange = options => {
    this.setState({
      loading: options.loading || false,
      error: options.error || false,
      formData: options.formData,
    });
  };

  handleSubmissionComplete = formData => {
    this.setState({
      loading: false,
      success: true,
      formData: formData,
    });
    this.props.onSuccess(false);
  };

  render() {
    if (this.state.success) {
      return this.renderSuccess(this.state.formData.full_name);
    } else if (this.state.loading) {
      return this.renderLoading();
    } else if (this.state.error) {
      return this.renderError(this.state.formData.full_name);
    } else {
      return this.renderFormWizard();
    }
  }

  renderFormWizard = () => {
    const { activeStep, slotSearchData, loading } = this.state;
    return (
      <React.Fragment>
        <DrawerHeader
          onBack={activeStep === DETAILS_STEP ? this.handleBack : null}
          onClose={this.props.handleClose}
          title={
            activeStep === SLOT_SEARCH_STEP
              ? getTranslatedString('showingDrawer.newShowing')
              : getTranslatedString('showingDrawer.additionalDetails')
          }
        />
        <DrawerBody>
          <Page show={activeStep === SLOT_SEARCH_STEP}>{this.renderSearchStep()}</Page>
          {activeStep === DETAILS_STEP && this.renderDetailsStep()}
        </DrawerBody>
        <DrawerFooter
          primaryActionFormName={activeStep === DETAILS_STEP ? FORM_NAME : undefined}
          primaryActionText={
            loading
              ? getTranslatedString('showingDrawer.loading')
              : activeStep === SLOT_SEARCH_STEP
                ? getTranslatedString('showingDrawer.continue')
                : getTranslatedString('showingDrawer.confirmAndBook')
          }
          onPrimaryActionClick={activeStep === SLOT_SEARCH_STEP ? this.goNextStep : undefined}
          primaryActionDisabled={!slotSearchData || loading}
        />
      </React.Fragment>
    );
  };

  renderSuccess = fullName => {
    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: successAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('showingDrawer.showingSuccessfullyBooked')}
        bodyText={getTranslatedString('showingDrawer.showingSuccessfullyBookedMessage', {
          name: fullName,
        })}
        cta={
          <CtaPrimary onClick={this.props.handleClose}>
            {getTranslatedString('showingDrawer.close')}
          </CtaPrimary>
        }
      />
    );
  };

  renderError = fullName => {
    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: errorAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('globalDrawer.errorHeader', {
          name: fullName,
        })}
        bodyText={getTranslatedString('globalDrawer.errorBody')}
        cta={
          <CtaPrimary onClick={() => this.setState({ error: false })}>
            {getTranslatedString('globalDrawer.goBack')}
          </CtaPrimary>
        }
      />
    );
  };

  renderLoading = () => {
    const defaultOptions = {
      loop: true,
      autoplay: true,
      animationData: loadingAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('globalDrawer.loadingHeader', {
          object: getTranslatedString('showing'),
        })}
        bodyText={getTranslatedString('globalDrawer.loadingBody')}
      />
    );
  };

  renderSearchStep = () => {
    return (
      <CreateShowingDrawerSearchStep
        showForceBooking={this.props.showForceBooking}
        getAgents={this.props.getAgents}
        agents={this.props.agents}
        properties={this.props.properties}
        unitTypes={this.props.unitTypes}
        setSlotSearchData={this.setSlotSearchData}
        getLandlordTrafficSources={this.props.getLandlordTrafficSources}
      />
    );
  };

  renderDetailsStep = () => {
    return (
      <CreateShowingDrawerDetailsStep
        slotSearchData={this.state.slotSearchData}
        trafficSources={this.props.trafficSources}
        formName={FORM_NAME}
        onLoadingChange={this.handleLoadingChange}
        onSubmissionComplete={this.handleSubmissionComplete}
      />
    );
  };
}

export default CreateShowingDrawerWizard;
